/**
 * LogService - Centralized logging for MCP calls, API calls, and DB operations
 * Supports both frontend in-memory logs and backend database logs
 */

export type LogLevel = 'info' | 'success' | 'warning' | 'error';

export interface LogEntry {
  id: string;
  timestamp: Date;
  action: string;
  level: LogLevel;
  source: string;
  message: string;
  details?: Record<string, unknown>;
  duration?: number;
  status?: 'pending' | 'success' | 'error';
  isBackendLog?: boolean; // Flag to distinguish backend logs from frontend logs
}

// Backend log format from database API
interface BackendLogEntry {
  id: string;
  service: string;
  level: LogLevel;
  action: string;
  message: string;
  metadata: string | null;
  created_at: string;
}

type LogListener = (logs: LogEntry[]) => void;

class LogService {
  private logs: LogEntry[] = [];
  private maxLogs = 500;
  private listeners: Set<LogListener> = new Set();
  private backendLogsCache: Map<string, LogEntry> = new Map(); // Cache backend logs by ID
  private fetchInterval: number | null = null;
  private isFetching = false;

  private generateId(): string {
    return `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private notify(): void {
    const logsCopy = [...this.logs];
    this.listeners.forEach((listener) => listener(logsCopy));
  }

  subscribe(listener: LogListener): () => void {
    this.listeners.add(listener);
    // Immediately send current logs
    listener([...this.logs]);
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  /**
   * Convert backend log format to frontend LogEntry format
   */
  private convertBackendLog(backendLog: BackendLogEntry): LogEntry {
    // Parse metadata JSON if present
    let details: Record<string, unknown> | undefined;
    if (backendLog.metadata) {
      try {
        details = JSON.parse(backendLog.metadata);
      } catch {
        details = { raw: backendLog.metadata };
      }
    }

    // Map level to status
    const statusMap: Record<LogLevel, 'pending' | 'success' | 'error' | undefined> = {
      'info': undefined,
      'success': 'success',
      'warning': undefined,
      'error': 'error',
    };

    return {
      id: backendLog.id,
      timestamp: new Date(backendLog.created_at),
      action: backendLog.action,
      level: backendLog.level,
      source: backendLog.service,
      message: backendLog.message,
      details,
      status: statusMap[backendLog.level],
      isBackendLog: true,
    };
  }

  /**
   * Fetch logs from backend database API
   */
  async fetchBackendLogs(options?: {
    service?: string;
    level?: LogLevel;
    limit?: number;
  }): Promise<void> {
    if (this.isFetching) return; // Prevent concurrent fetches

    this.isFetching = true;
    try {
      const databaseUrl = import.meta.env.VITE_DATABASE_SERVICE_URL;
      if (!databaseUrl) {
        console.warn('VITE_DATABASE_SERVICE_URL not configured, skipping backend log fetch');
        return;
      }

      // Build query params
      const params = new URLSearchParams();
      if (options?.service) params.append('service', options.service);
      if (options?.level) params.append('level', options.level);
      params.append('limit', String(options?.limit || 100));

      const url = `${databaseUrl}/api/logs?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`Failed to fetch backend logs: HTTP ${response.status}`);
        return;
      }

      const result = await response.json() as { success: boolean; data?: BackendLogEntry[]; error?: string };
      if (!result.success) {
        console.error('Backend logs API returned unsuccessful response:', result.error);
        return;
      }

      if (!result.data || !Array.isArray(result.data)) {
        console.error('Backend logs API returned invalid data format:', result.data);
        return;
      }

      // Convert backend logs to frontend format
      const backendLogs = result.data.map((log) => this.convertBackendLog(log));

      // Merge with existing logs, avoiding duplicates
      const mergedLogs = [...this.logs];
      const existingIds = new Set(this.logs.map((l) => l.id));

      for (const backendLog of backendLogs) {
        if (!existingIds.has(backendLog.id)) {
          mergedLogs.push(backendLog);
          this.backendLogsCache.set(backendLog.id, backendLog);
        }
      }

      // Sort by timestamp (newest first) and trim
      mergedLogs.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      this.logs = mergedLogs.slice(0, this.maxLogs);

      this.notify();
    } catch (error) {
      console.error('Error fetching backend logs:', error);
    } finally {
      this.isFetching = false;
    }
  }

  /**
   * Start auto-refresh of backend logs
   */
  startAutoRefresh(intervalMs: number = 5000): void {
    if (this.fetchInterval !== null) {
      this.stopAutoRefresh();
    }

    // Initial fetch
    this.fetchBackendLogs();

    // Set up interval
    this.fetchInterval = window.setInterval(() => {
      this.fetchBackendLogs();
    }, intervalMs);
  }

  /**
   * Stop auto-refresh of backend logs
   */
  stopAutoRefresh(): void {
    if (this.fetchInterval !== null) {
      window.clearInterval(this.fetchInterval);
      this.fetchInterval = null;
    }
  }

  log(
    action: string,
    level: LogLevel,
    source: string,
    message: string,
    details?: Record<string, unknown>,
    status?: 'pending' | 'success' | 'error'
  ): string {
    const id = this.generateId();
    const entry: LogEntry = {
      id,
      timestamp: new Date(),
      action,
      level,
      source,
      message,
      details,
      status,
      isBackendLog: false,
    };

    this.logs.unshift(entry); // Add to beginning for newest first

    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    this.notify();
    return id;
  }

  updateLog(
    id: string,
    updates: Partial<Pick<LogEntry, 'status' | 'duration' | 'message' | 'details'>>
  ): void {
    const logIndex = this.logs.findIndex((l) => l.id === id);
    if (logIndex !== -1) {
      this.logs[logIndex] = { ...this.logs[logIndex], ...updates };
      this.notify();
    }
  }

  // Convenience methods
  mcp(tool: string, args?: Record<string, unknown>, status?: 'pending' | 'success' | 'error'): string {
    const level: LogLevel = status === 'error' ? 'error' : status === 'success' ? 'success' : 'info';
    return this.log(`mcp_${tool}`, level, 'MCP Tool', `${tool}`, { args }, status);
  }

  api(method: string, endpoint: string, details?: Record<string, unknown>, status?: 'pending' | 'success' | 'error'): string {
    const level: LogLevel = status === 'error' ? 'error' : status === 'success' ? 'success' : 'info';
    return this.log(`api_${method}_${endpoint}`, level, 'API', `${method} ${endpoint}`, details, status);
  }

  db(operation: string, table: string, details?: Record<string, unknown>, status?: 'pending' | 'success' | 'error'): string {
    const level: LogLevel = status === 'error' ? 'error' : status === 'success' ? 'success' : 'info';
    return this.log(`db_${operation}_${table}`, level, 'Database', `${operation} on ${table}`, details, status);
  }

  info(source: string, message: string, details?: Record<string, unknown>): string {
    return this.log(`${source.toLowerCase()}_info`, 'info', source, message, details);
  }

  error(source: string, message: string, details?: Record<string, unknown>): string {
    return this.log(`${source.toLowerCase()}_error`, 'error', source, message, details, 'error');
  }

  warning(source: string, message: string, details?: Record<string, unknown>): string {
    return this.log(`${source.toLowerCase()}_warning`, 'warning', source, message, details);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter((l) => l.level === level);
  }

  getLogsByActionPrefix(prefix: string): LogEntry[] {
    return this.logs.filter((l) => l.action.startsWith(`${prefix}_`));
  }

  clearLogs(): void {
    // Clear only frontend logs, keep backend logs
    this.logs = this.logs.filter((l) => l.isBackendLog);
    this.notify();
  }

  async clearAllLogs(): Promise<void> {
    // Clear frontend logs immediately
    this.logs = [];
    this.backendLogsCache.clear();
    this.notify();

    // Also delete backend logs from D1 database
    try {
      const databaseUrl = import.meta.env.VITE_DATABASE_SERVICE_URL;
      if (databaseUrl) {
        const response = await fetch(`${databaseUrl}/api/logs`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          console.warn('Failed to delete backend logs from D1:', response.statusText);
        }
      }
    } catch (error) {
      console.error('Error deleting backend logs:', error);
    }
  }
}

// Singleton instance
export const logService = new LogService();

// Export for convenience
export default logService;
